import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Empty, Input, Popconfirm, Row, Select, Space, Table, Tag, Typography, message } from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined, TeamOutlined, UserDeleteOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { ModalComponent, CancelButton } from "@comps/components";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { getEmployeesReq, getSalaryPolicyByEmpId, updateSalaryPolicyReq } from "../../employeeManagement/companyEmployees/requests";
import { getResourceDataReq } from "@comps/commonFunctions";
import UpdatePayrollPolicy from "../../employeeManagement/companyEmployees/components/UpdatePayrollPolicy";

const { Text } = Typography;

const mapSalaryBenefits = (salaryBenefits = []) => (
  salaryBenefits.map((item) => ({
    grade_benefit_id: item.grade_benefit_id,
    benefit_group_ids: item.benefit_group_ids || [],
    benefit_details: typeof item.benefit_details === "string"
      ? item.benefit_details
      : JSON.stringify(item.benefit_details || []),
  }))
);

const PayrollEmployeePolicyEditorModal = ({ employmentId, onClose, onRefresh }) => (
  <ModalComponent
    top={ 20 }
    width={ 1200 }
    maskClosable={ false }
    visible={ true }
    footer={ [<CancelButton key="close_button" onClick={ onClose } />] }
    onCancel={ onClose }
    mainTitle="Manage Employee Payroll"
    subTitle="Update payroll assignment and policy"
  >
    <UpdatePayrollPolicy key={ employmentId } id={ employmentId } />
    <div style={ { display: "flex", justifyContent: "flex-end", marginTop: 16 } }>
      <Button type="primary" onClick={ () => {
        onRefresh?.();
        onClose();
      } }>
        Done
      </Button>
    </div>
  </ModalComponent>
);

const PayrollEmployeesManager = (props) => {
  const [loader, setLoader] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
    pageSize: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [columnSearchText, setColumnSearchText] = useState("");
  const [quickMatches, setQuickMatches] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmploymentId, setSelectedEmploymentId] = useState(null);
  const [childComponent, setChildComponent] = useState(null);

  useEffect(() => {
    getPayrollEmployees();
    getAvailableEmployees();
    // eslint-disable-next-line
  }, [currentPage]);

  const getPayrollEmployees = () => {
    const payload = {
      page: currentPage,
      filters: `payroll_|${props.record.id};employee_status_|active`,
      showActive: true,
    };

    makeRequest(setLoader, getEmployeesReq, payload, onPayrollEmployeesSuccess, onError);
  };

  const onPayrollEmployeesSuccess = (response) => {
    const list = response?.employee_employments?.data || [];
    const meta = response?.employee_employments?.meta?.pagination || {};

    setEmployees(list);
    setPagination({
      total: meta.total || 0,
      current: meta.current_page || currentPage,
      pageSize: meta.per_page || 10,
    });
  };

  const getAvailableEmployees = (search = "") => {
    const payload = {
      page: 1,
      type: "employment",
      filters: search ? `search_|${search};` : "",
    };

    makeRequestStateless(getResourceDataReq, payload, (response) => {
      setAvailableEmployees(response?.resources?.data || []);
    }, () => {
      setAvailableEmployees([]);
    });
  };

  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Something went wrong");
  };

  const onQuickSearch = (value) => {
    const query = value.trim();
    setSearchText(query);

    if (!query) {
      setQuickMatches([]);
      return;
    }

    const payload = {
      page: 1,
      type: "employment",
      filters: `search_|${query};`,
    };

    makeRequestStateless(getResourceDataReq, payload, (response) => {
      const results = response?.resources?.data || [];
      setQuickMatches(results);
      setAvailableEmployees(results);
    }, () => setQuickMatches([]));
  };

  const openManageEmployee = (employmentId) => {
    setChildComponent(
      <PayrollEmployeePolicyEditorModal
        employmentId={ employmentId }
        onClose={ () => {
          setChildComponent(null);
          getPayrollEmployees();
          props.onRefresh?.();
        } }
        onRefresh={ () => {
          getPayrollEmployees();
          props.onRefresh?.();
        } }
      />
    );
  };

  const updateEmployeePayroll = (employmentId, payrollId) => {
    makeRequest(setLoader, getSalaryPolicyByEmpId, employmentId, (data) => {
      const payload = {
        employmentId,
        payroll_info: {
          payroll_id: payrollId,
          type: data?.salary?.type,
          payment_method_id: data?.salary?.payment_method_id,
          gross_salary: data?.salary?.gross_salary,
          policy_id: data?.policy?.policy_planer_id || null,
        },
        salary_benefits: mapSalaryBenefits(data?.salary?.salary_benefits),
      };

      makeRequest(setLoader, updateSalaryPolicyReq, payload, () => {
        message.success(payrollId ? "Employee added to payroll" : "Employee removed from payroll");
        getPayrollEmployees();
        getAvailableEmployees();
        props.onRefresh?.();
      }, onError);
    }, onError);
  };

  const filteredEmployees = useMemo(() => {
    const keyword = (columnSearchText || searchText || "").toLowerCase().trim();

    if (!keyword) {
      return employees;
    }

    return employees.filter((employee) => (
      `${employee.full_name_code || ""} ${employee.employee_name || ""} ${employee.code || ""} ${employee.branch_name || ""} ${employee.department_name || ""} ${employee.designation_name || ""}`
        .toLowerCase()
        .includes(keyword)
    ));
  }, [employees, searchText, columnSearchText]);

  const assignedEmployeeIds = useMemo(() => employees.map((employee) => employee.id), [employees]);

  const availableEmployeeOptions = useMemo(() => (
    availableEmployees
      .filter((employee) => !assignedEmployeeIds.includes(employee.id))
      .map((employee) => ({
        value: employee.id,
        label: employee.full_name_code || employee.identity || employee.full_name,
      }))
  ), [availableEmployees, assignedEmployeeIds]);

  const columns = [
    {
      title: "Employee",
      key: "employee",
      filterDropdown: () => (
        <div style={ { padding: 8, width: 220 } }>
          <Input
            autoFocus
            allowClear
            placeholder="Filter by name or code"
            value={ columnSearchText }
            onChange={ (event) => setColumnSearchText(event.target.value) }
          />
          <Space style={ { marginTop: 8, width: "100%", justifyContent: "space-between" } }>
            <Button size="small" type="primary" onClick={ () => undefined }>
              Apply
            </Button>
            <Button size="small" onClick={ () => setColumnSearchText("") }>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: () => <SearchOutlined style={ { color: columnSearchText ? "#1677ff" : undefined } } />,
      render: (record) => (
        <div>
          <div style={ { fontWeight: 600, color: "#111827" } }>{ record.full_name_code || record.employee_name }</div>
          <Text type="secondary">{ record.code ? `Code: ${record.code}` : (record.designation_name || "No designation") }</Text>
        </div>
      ),
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space wrap>
          <Button icon={ <UserSwitchOutlined /> } onClick={ () => openManageEmployee(record.id) }>
            Manage
          </Button>
          <Popconfirm
            title="Remove employee from this payroll?"
            onConfirm={ () => updateEmployeePayroll(record.id, null) }
          >
            <Button danger icon={ <UserDeleteOutlined /> }>Remove</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const footer = [
    <CancelButton key="close_button" onClick={ props.onCompleted } />,
  ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 1280 }
      maskClosable={ false }
      visible={ true }
      footer={ footer }
      onCancel={ props.onCompleted }
      mainTitle={ `${props.record?.name || "Payroll"} Employees` }
      subTitle="View, add, and remove employees from this payroll"
    >
      { childComponent }
      <div className="payroll-workspace__section">
        <Row gutter={ [16, 16] }>
          <Col xs={ 24 } xl={ 8 }>
            <Card
              bordered={ false }
              className="payroll-workspace__detail-card"
              title="Add employee to payroll"
              extra={ <Tag color="blue">{ props.record?.emp_count || 0 } assigned</Tag> }
            >
              <Space direction="vertical" size={ 12 } style={ { width: "100%" } }>
                <Select
                  showSearch
                  allowClear
                  value={ selectedEmploymentId }
                  placeholder="Select employee to add"
                  style={ { width: "100%" } }
                  options={ availableEmployeeOptions }
                  onChange={ (value) => setSelectedEmploymentId(value || null) }
                  onSearch={ (value) => {
                    onQuickSearch(value);
                    getAvailableEmployees(value);
                  } }
                  filterOption={ false }
                  notFoundContent="No employees found"
                />
                {/* <Input
                  prefix={ <SearchOutlined /> }
                  placeholder="Filter current payroll employees"
                  value={ searchText }
                  onChange={ (event) => setSearchText(event.target.value) }
                /> */}
                { quickMatches.length > 0 && (
                  <Card size="small" style={ { background: "#f8fafc", borderRadius: 12 } }>
                    <Space wrap>
                      { quickMatches
                        .filter((employee) => !assignedEmployeeIds.includes(employee.id))
                        .slice(0, 10)
                        .map((employee) => (
                          <Button
                            key={ employee.id }
                            type={ selectedEmploymentId === employee.id ? "primary" : "default" }
                            onClick={ () => setSelectedEmploymentId(employee.id) }
                          >
                            { employee.full_name_code || employee.identity || employee.full_name }
                          </Button>
                        )) }
                    </Space>
                  </Card>
                ) }
                { availableEmployeeOptions.length < 1 && (
                  <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No available employees found to add into this payroll." />
                ) }
                <Space wrap>
                  <Button
                    type="primary"
                    icon={ <PlusOutlined /> }
                    disabled={ !selectedEmploymentId }
                    onClick={ () => updateEmployeePayroll(selectedEmploymentId, props.record.id) }
                  >
                    Add to this payroll
                  </Button>
                  <Button icon={ <ReloadOutlined /> } onClick={ getPayrollEmployees }>Refresh list</Button>
                </Space>
              </Space>
            </Card>
          </Col>

          <Col xs={ 24 } xl={ 16 }>
            <Card
              bordered={ false }
              className="payroll-workspace__detail-card"
              title="Employees in this payroll"
              extra={ <Tag color="green"><TeamOutlined /> { pagination.total || filteredEmployees.length }</Tag> }
            >
              <Table
                rowKey="id"
                columns={ columns }
                dataSource={ filteredEmployees }
                loading={ loader }
                pagination={ {
                  total: pagination.total,
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  onChange: setCurrentPage,
                } }
                locale={ {
                  emptyText: <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No employees found in this payroll." />,
                } }
              />
            </Card>
          </Col>
        </Row>
      </div>
    </ModalComponent>
  );
};

export default PayrollEmployeesManager;
